<template>
	<v-row class="full-height">
		<v-col cols="12" class="flex-none">
			<div class="customer-actions">
				<v-btn
					v-if="customerData.customerId"
					depressed
					outlined
					color="error"
					class="mr-auto"
					@click="showDeleteCustomerDialog = true"
				>
					Delete Customer
				</v-btn>
				<v-btn
					v-if="!returnToQuote"
					depressed
					outlined
					class="mr-2"
					@click="$router.push('/relationships/customer')"
				>
					Cancel
				</v-btn>
				<v-btn v-if="returnToQuote" depressed outlined class="mr-2" @click="$router.back()">
					Cancel
				</v-btn>
				<v-btn
					v-if="!returnToQuote && !addressTab"
					depressed
					outlined
					:loading="savingCustomer"
					:disabled="savingCustomer"
					@click="handleSaveCustomer()"
				>
					Save Customer
				</v-btn>
				<v-btn
					v-if="quoteId || orderId || returnToQuote || $route.query.addressTab === 'true'"
					depressed
					outlined
					:loading="savingCustomer"
					:disabled="savingCustomer"
					@click="handleSaveCustomerWithReturnToQuote()"
				>
					{{ orderId ? 'Save & Back to Order' : 'Save & Back to Quote' }}
				</v-btn>
			</div>

			<!-- Delete Customer Confirmation Dialog -->
			<v-dialog v-model="showDeleteCustomerDialog" max-width="450">
				<v-card>
					<v-card-title class="text-h6">Delete Customer</v-card-title>
					<v-card-text style="font-size: 1.6rem">
						Are you sure you want to delete this customer? This action cannot be undone.
					</v-card-text>
					<v-card-actions>
						<v-spacer></v-spacer>
						<v-btn text @click="showDeleteCustomerDialog = false">Cancel</v-btn>
						<v-btn color="error" text @click="deleteCustomer()" :loading="deletingCustomer">Delete</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>

			<!-- Delete Record Confirmation Dialog -->
			<v-dialog v-model="showDeleteRecordDialog" max-width="450">
				<v-card>
					<v-card-title class="text-h6">Delete {{ pendingDeleteType }}</v-card-title>
					<v-card-text style="font-size: 1.6rem">
						Are you sure you want to delete this {{ pendingDeleteType.toLowerCase() }}?
					</v-card-text>
					<v-card-actions>
						<v-spacer></v-spacer>
						<v-btn text @click="cancelDeleteRecord()">Cancel</v-btn>
						<v-btn color="error" text @click="confirmDeleteRecord()">Delete</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>

			<!-- Error Snackbar -->
			<v-snackbar v-model="deleteError" color="error" :timeout="6000" top>
				<span style="font-size: 1.6rem">{{ deleteErrorMessage }}</span>
				<template v-slot:action="{ attrs }">
					<v-btn text v-bind="attrs" @click="deleteError = false">Close</v-btn>
				</template>
			</v-snackbar>

			<div class="customer-form">
				<v-row>
					<v-col>
						<v-form ref="organizationRef">
							<div class="form-input">
								<label>Organization</label>
								<v-text-field
									outlined
									dense
									hide-details="auto"
									v-model="customerData.organization"
									:rules="[(value) => !!value || '* Please enter an organization']"
								></v-text-field>
							</div>
							<div class="form-input">
								<label>Sales Tax</label>
								<v-select
									v-model="customerData.salesTaxId"
									:items="taxes"
									item-text="text"
									item-value="salesTaxId"
									dense
									outlined
									hide-details="auto"
									:rules="[
										(value) =>
											(value !== null && value !== undefined) ||
											'* Please select a sales tax profile.',
									]"
								></v-select>
							</div>
							<div v-if="customerData.salesTaxId === 0" class="customer-form-upload">
								<a
									v-if="uploadedFile && uploadedFileLink"
									:href="`https://drive.google.com/file/d/${uploadedFileLink.id}/view`"
									target="_blank"
								>
									{{ uploadedFileLink.name }}
								</a>
								<label v-else for="file-input" class="form-upload-link">
									<input type="file" id="file-input" @change="onFileUpload" style="display: none" />
									<v-icon>mdi-upload</v-icon>
									Upload sales tax exempt form
								</label>
							</div>
						</v-form>
					</v-col>

					<v-col>
						<div class="form-input">
							<label>Customer Number</label>
							<v-text-field
								outlined
								dense
								hide-details="auto"
								v-model="customerData.customerNumber"
								:disabled="!customerData.customerId"
							></v-text-field>
						</div>
						<div class="form-input">
							<label>Shipping Method</label>
							<v-select
								v-model="customerData.shippingMethodId"
								:items="shippingMethods"
								item-text="shippingMethodName"
								item-value="shippingMethodId"
								dense
								outlined
								hide-details="auto"
							></v-select>
						</div>
					</v-col>

					<v-col>
						<div class="form-input">
							<label>Customer Category</label>
							<v-select
								v-model="customerData.customerCategoryId"
								:items="categories"
								item-text="category"
								item-value="customerCategoryId"
								dense
								outlined
								hide-details="auto"
							></v-select>
						</div>
						<div class="form-input">
							<label>Payment Terms</label>
							<v-select
								v-model="customerData.paymentTermsId"
								:items="paymentTerms"
								item-text="paymentTermsName"
								item-value="paymentTermsId"
								dense
								outlined
								hide-details="auto"
							></v-select>
						</div>
					</v-col>

					<v-col>
						<v-form ref="salesRepRef">
							<div class="form-input">
								<label>Sales Rep</label>
								<v-select
									v-model="customerData.salesRepId"
									:items="salesReps"
									item-text="name"
									item-value="salesRepId"
									dense
									outlined
									hide-details="auto"
									:rules="[
										(value) =>
											(value !== null && value !== undefined) || '* Please select a sales rep.',
									]"
								></v-select>
							</div>
							<div class="form-input">
								<label>Payment Method</label>
								<v-select
									v-model="customerData.paymentMethodId"
									:items="paymentMethods"
									item-text="paymentMethodName"
									item-value="paymentMethodId"
									dense
									outlined
									hide-details="auto"
								></v-select>
							</div>
						</v-form>
					</v-col>
				</v-row>
			</div>
		</v-col>

		<v-col cols="12" class="flex-auto">
			<ul class="customer-tabs">
				<li
					class="customer-tabs-tab"
					:class="activeTab === 1 ? 'active' : null"
					@click="activeTab = 1"
				>
					Contacts
				</li>
				<li
					class="customer-tabs-tab"
					:class="activeTab === 2 ? 'active' : null"
					@click="activeTab = 2"
				>
					Addresses
				</li>
				<li
					class="customer-tabs-tab"
					:class="activeTab === 3 ? 'active' : null"
					@click="activeTab = 3"
				>
					Notes
				</li>
				<li
					v-show="false"
					class="customer-tabs-tab"
					:class="activeTab === 4 ? 'active' : null"
					@click="activeTab = 4"
				>
					Orders
				</li>
			</ul>

			<div class="table">
				<div v-if="activeTab === 1" class="table-container">
					<v-form ref="contactRef">
						<table>
							<thead>
								<tr>
									<th style="width: 10%" class="text-center">Primary Contact</th>
									<th style="width: 15%">First Name</th>
									<th style="width: 15%">Last Name</th>
									<th style="width: 15%">Work Phone Number</th>
									<th style="width: 15%">Cell Phone Number</th>
									<th style="width: 16%">Email address</th>
									<th style="width: 10%" class="text-center">Inactive</th>
									<th style="width: 2%">&nbsp;</th>
									<th style="width: 2%">&nbsp;</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(data, i) in contactData" :key="i">
									<template
										v-if="data == activeEdit || (contactData.length === i + 1 && noInitContacts)"
									>
										<td class="pl-0">
											<v-checkbox
												class="form-checkbox-center"
												v-model="data.primaryContact"
												color="accent"
												hide-details="auto"
											></v-checkbox>
										</td>
										<td>
											<div class="form-input">
												<v-text-field
													outlined
													dense
													hide-details="auto"
													v-model="data.firstName"
													:rules="[(value) => !!value || '* Please provide a name.']"
												></v-text-field>
											</div>
										</td>
										<td>
											<div class="form-input">
												<v-text-field
													outlined
													dense
													hide-details="auto"
													v-model="data.lastName"
												></v-text-field>
											</div>
										</td>
										<td>
											<div class="form-input">
												<v-text-field
													outlined
													dense
													hide-details="auto"
													v-model="data.workPhoneNumber"
												></v-text-field>
											</div>
										</td>
										<td>
											<div class="form-input">
												<v-text-field
													outlined
													dense
													hide-details="auto"
													v-model="data.cellPhoneNumber"
												></v-text-field>
											</div>
										</td>
										<td>
											<div class="form-input">
												<v-text-field
													outlined
													dense
													hide-details="auto"
													v-model="data.email"
													:rules="[(value) => !!value || '* Please provide an email.']"
												></v-text-field>
											</div>
										</td>
										<td class="pl-0">
											<v-checkbox
												class="form-checkbox-center"
												v-model="data.isActive"
												:false-value="true"
												:true-value="false"
												color="accent"
												hide-details="auto"
											></v-checkbox>
										</td>
										<td>
											<v-btn icon color="primary" @click="activeEdit = null"
												><v-icon style="font-size: 2rem">mdi-check</v-icon>
											</v-btn>
										</td>
										<td>
											<v-btn
												icon
												color="primary"
												@click="removeContact(data)"
												><v-icon style="font-size: 2rem">mdi-delete</v-icon>
											</v-btn>
										</td>
									</template>
									<template v-else>
										<td class="pl-0">
											<v-checkbox
												disabled
												class="form-checkbox-center"
												v-model="data.primaryContact"
												color="accent"
												hide-details="auto"
												readonly
											></v-checkbox>
										</td>
										<td>
											{{ data.firstName }}
										</td>
										<td>
											{{ data.lastName }}
										</td>
										<td>
											{{ data.workPhoneNumber }}
										</td>
										<td>
											{{ data.cellPhoneNumber }}
										</td>
										<td>
											{{ data.email }}
										</td>
										<td class="pl-0">
											<v-checkbox
												disabled
												class="form-checkbox-center"
												v-model="data.isActive"
												:false-value="true"
												:true-value="false"
												color="accent"
												hide-details="auto"
												readonly
											></v-checkbox>
										</td>
										<td>
											<v-btn icon color="primary" @click="activeEdit = data"
												><v-icon style="font-size: 2rem">mdi-pencil</v-icon>
											</v-btn>
										</td>
										<td>
											<v-btn
												icon
												color="primary"
												@click="removeContact(data)"
												><v-icon style="font-size: 2rem">mdi-delete</v-icon>
											</v-btn>
										</td>
									</template>
								</tr>
								<tr>
									<td>
										<v-btn class="pl-0 pr-1" text color="accent" @click="addContact()"
											><v-icon style="font-size: 2rem">mdi-plus</v-icon> Add a contact
										</v-btn>
									</td>
									<td v-if="noContacts" colspan="2">
										<p class="form-error text-center">* Please provide at least one contact.</p>
									</td>
								</tr>
							</tbody>
						</table>
					</v-form>
				</div>
				<div v-if="activeTab === 2" class="table-container">
					<table>
						<thead>
							<tr>
								<th style="width: 5%" class="text-center">Primary</th>
								<th style="width: 10%">Label</th>
								<th style="width: 10%">Address Type</th>
								<th style="width: 14%">Contact</th>
								<th style="width: 14%">Organization</th>
								<th style="width: 8%">Dept</th>
								<th style="width: 13%">Address Line 1</th>
								<th style="width: 10%">City</th>
								<th style="width: 7%">State</th>
								<th style="width: 7%">Zip Code</th>
								<th style="width: 5%" class="text-center">Inactive</th>
								<th style="width: 2%">&nbsp;</th>
								<th style="width: 2%">&nbsp;</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="(data, i) in addressData" :key="i">
								<template v-if="data == activeEdit">
									<td class="pl-0">
										<v-checkbox
											class="form-checkbox-center"
											v-model="data.isPrimaryAddress"
											color="accent"
											hide-details="auto"
										></v-checkbox>
									</td>
									<td>
										<div class="form-input">
											<v-select
												v-model="data.label"
												:items="addressLabels"
												dense
												outlined
												hide-details="auto"
											></v-select>
										</div>
									</td>
									<td>
										<div class="form-input">
											<v-select
												v-model="data.addressTypeId"
												:items="addressTypes"
												item-text="text"
												item-value="addressTypeId"
												dense
												outlined
												hide-details="auto"
											></v-select>
										</div>
									</td>
									<td>
										<div class="form-input">
											<v-select
												v-model="data.contactId"
												:items="contactData.filter(c => c.isActive !== false)"
												item-value="contactId"
												dense
												outlined
												hide-details="auto"
												clearable
											>
												<template v-slot:selection="{ item }">
													<span class="text-truncate d-inline-block" style="max-width: 100%">{{ item.firstName }} {{ item.lastName }}</span>
												</template>
												<template v-slot:item="{ item }">{{ item.firstName }} {{ item.lastName }}</template>
											</v-select>
										</div>
									</td>
									<td>
										<div class="form-input">
											<v-text-field
												outlined
												dense
												hide-details="auto"
												v-model="data.organization"
												:placeholder="customerData.organization"
												clearable
											></v-text-field>
										</div>
									</td>
									<td>
										<div class="form-input"
											v-if="data.label && (data.label.toUpperCase() === 'BILL TO' || data.label.toUpperCase() === 'BOTH')">
											<v-text-field
												outlined
												dense
												hide-details="auto"
												v-model="data.department"
											></v-text-field>
										</div>
									</td>
									<td>
										<div class="form-input">
											<v-text-field
												outlined
												dense
												hide-details="auto"
												v-model="data.addressLine1"
											></v-text-field>
										</div>
									</td>
									<td>
										<div class="form-input">
											<v-text-field
												outlined
												dense
												hide-details="auto"
												v-model="data.city"
											></v-text-field>
										</div>
									</td>
									<td>
										<div class="form-input">
											<v-select
												v-model="data.state"
												:items="states"
												item-text="text"
												item-value="value"
												dense
												outlined
												hide-details="auto"
											></v-select>
										</div>
									</td>
									<td>
										<div class="form-input">
											<v-text-field
												outlined
												dense
												hide-details="auto"
												v-model="data.postalCode"
											></v-text-field>
										</div>
									</td>
									<td class="pl-0">
										<v-checkbox
											class="form-checkbox-center"
											v-model="data.isActive"
											:false-value="true"
											:true-value="false"
											color="accent"
											hide-details="auto"
										></v-checkbox>
									</td>
									<td>
										<v-btn icon color="primary" @click="activeEdit = null"
											><v-icon style="font-size: 2rem">mdi-check</v-icon>
										</v-btn>
									</td>
									<td>
										<v-btn icon color="primary" @click="removeAddress(data)"
											><v-icon style="font-size: 2rem">mdi-delete</v-icon>
										</v-btn>
									</td>
								</template>
								<template v-else>
									<td class="pl-0">
										<v-checkbox
											disabled
											class="form-checkbox-center"
											v-model="data.isPrimaryAddress"
											color="accent"
											hide-details="auto"
											readonly
										></v-checkbox>
									</td>
									<td>
										<span v-if="data.label" class="table-label">{{ data.label }}</span>
									</td>
									<td>
										{{ renderAddress(data.addressTypeId) }}
									</td>
									<td>{{ getContactName(data.contactId) }}</td>
									<td>{{ data.organization }}</td>
									<td>{{ (data.label && (data.label.toUpperCase() === 'BILL TO' || data.label.toUpperCase() === 'BOTH')) ? data.department : '' }}</td>
									<td>
										{{ data.addressLine1 }}
									</td>
									<td>
										{{ data.city }}
									</td>
									<td>
										{{ data.state }}
									</td>
									<td>
										{{ data.postalCode }}
									</td>
									<td class="pl-0">
										<v-checkbox
											disabled
											class="form-checkbox-center"
											v-model="data.isActive"
											:false-value="true"
											:true-value="false"
											color="accent"
											hide-details="auto"
											readonly
										></v-checkbox>
									</td>
									<td>
										<v-btn icon color="primary" @click="activeEdit = data"
											><v-icon style="font-size: 2rem">mdi-pencil</v-icon>
										</v-btn>
									</td>
									<td>
										<v-btn icon color="primary" @click="removeAddress(data)"
											><v-icon style="font-size: 2rem">mdi-delete</v-icon>
										</v-btn>
									</td>
								</template>
							</tr>
							<tr>
								<td>
									<v-btn
										:class="addressTab ? 'pl-0 pr-1 pulse-effect' : 'pl-0 pr-1'"
										text
										color="accent"
										@click="addAddress()"
									>
										<v-icon style="font-size: 2rem">mdi-plus</v-icon> Add an address
									</v-btn>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
				<div v-if="activeTab === 3" class="table-container">
					<table>
						<thead>
							<tr>
								<th style="width: 60%">Note</th>
								<th style="width: 20%">Added By</th>
								<th style="width: 16%">Date/Time</th>
								<th style="width: 2%">&nbsp;</th>
								<th style="width: 2%">&nbsp;</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="(data, i) in noteData" :key="i">
								<template v-if="data == activeEdit">
									<td>
										<div class="form-input">
											<v-text-field
												outlined
												dense
												hide-details="auto"
												v-model="data.note"
											></v-text-field>
										</div>
									</td>
									<td>
										<p>{{ data.customerId }}</p>
									</td>
									<td>
										<p>{{ formatDateLocal(data.date) }}</p>
									</td>
									<td>
										<v-btn icon color="primary" @click="activeEdit = null"
											><v-icon style="font-size: 2rem">mdi-check</v-icon>
										</v-btn>
									</td>
									<td>
										<v-btn icon color="primary" @click="removeNote(data)"
											><v-icon style="font-size: 2rem">mdi-delete</v-icon>
										</v-btn>
									</td>
								</template>
								<template v-else>
									<td>
										{{ data.note }}
									</td>
									<td>
										<p>{{ data.customerId }}</p>
									</td>
									<td>
										<p>{{ formatDateLocal(data.date) }}</p>
									</td>
									<td>
										<v-btn icon color="primary" @click="activeEdit = data"
											><v-icon style="font-size: 2rem">mdi-pencil</v-icon>
										</v-btn>
									</td>
									<td>
										<v-btn icon color="primary" @click="removeNote(data)"
											><v-icon style="font-size: 2rem">mdi-delete</v-icon>
										</v-btn>
									</td>
								</template>
							</tr>
							<tr>
								<td>
									<v-btn class="pl-0 pr-1" text color="accent" @click="addNote()"
										><v-icon style="font-size: 2rem">mdi-plus</v-icon> Add a note
									</v-btn>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
				<div v-if="activeTab === 4" class="table-container">
					<table>
						<thead>
							<tr>
								<th style="width: 7%">Order#</th>
								<th style="width: 9%">Type</th>
								<th style="width: 9%">Order Date</th>
								<th style="width: 5%">REP</th>
								<th style="width: 6.5%" class="text-center">Status</th>
								<th style="width: 11.5%" class="text-center">Status Memo</th>
								<th style="width: 13%" class="text-center">Completed Date</th>
								<th style="width: 6.5%">Source</th>
								<th style="width: 11%">Customer PO</th>
								<th style="width: 8.5%">Balance</th>
								<th style="width: 13%">Last Invoice Date</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="(customer, i) in orderData" :key="i">
								<td>{{ customer.orderNumber }}</td>
								<td>{{ customer.type }}</td>
								<td>{{ customer.orderDate }}</td>
								<td>{{ customer.rep }}</td>
								<td class="text-center">
									<div class="table-status">
										{{ customer.status }}
									</div>
								</td>
								<td class="text-center">
									<div class="table-status">
										{{ customer.statusMemo }}
									</div>
								</td>
								<td class="text-center">{{ customer.completedDate }}</td>
								<td>{{ customer.source }}</td>
								<td>{{ customer.customerPo }}</td>
								<td>{{ customer.balance == "" ? "-" : customer.balance }}</td>
								<td>{{ customer.invoiceDate }}</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</v-col>
	</v-row>
</template>

<script>
import gql from "graphql-tag";
import DateFormatting from "@/mixins/DateFormatting";
import { TAX_TYPES } from "@/helpers/constant";
import { getApiUrl } from "@/helpers";
export default {
	name: "CustomerView",
	mixins: [DateFormatting],
	props: {
		customerId: {
			type: Number,
			default: null,
		},
		addressTab: {
			type: Boolean,
			default: false,
		},
		returnToQuote: {
			type: Boolean,
			default: false,
		},
		quoteId: {
			type: Number,
			default: null,
		},
		orderId: {
			type: [Number, String],
			default: null,
		},
	},
	data() {
		return {
			activeEdit: null,
			uploadedFile: null,
			uploadedFileLink: "",
			customerData: {
				customerId: null,
				organization: "",
				salesTaxId: null,
				customerNumber: "",
				shippingMethodId: null,
				customerCategoryId: null,
				paymentTermsId: null,
				salesRepId: null,
				paymentMethodId: null,
			},
			taxes: TAX_TYPES,
			shippingMethods: [],
			categories: [],
			paymentTerms: [],
			salesReps: [],
			paymentMethods: [],
			activeTab: 1,
			contactData: [],
			addressData: [],
			addressLabels: ["BILL TO", "SHIP TO", "BOTH"],
			addressTypes: [
				{ text: "COMMERCIAL", addressTypeId: 0 },
				{ text: "RESIDENTIAL", addressTypeId: 1 },
			],
			noteData: [],
			orderData: [],
			states: [
				{ value: "AL", text: "Alabama" },
				{ value: "AK", text: "Alaska" },
				{ value: "AZ", text: "Arizona" },
				{ value: "AR", text: "Arkansas" },
				{ value: "CA", text: "California" },
				{ value: "CO", text: "Colorado" },
				{ value: "CT", text: "Connecticut" },
				{ value: "DE", text: "Delaware" },
				{ value: "DC", text: "District Of Columbia" },
				{ value: "FL", text: "Florida" },
				{ value: "GA", text: "Georgia" },
				{ value: "HI", text: "Hawaii" },
				{ value: "ID", text: "Idaho" },
				{ value: "IL", text: "Illinois" },
				{ value: "IN", text: "Indiana" },
				{ value: "IA", text: "Iowa" },
				{ value: "KS", text: "Kansas" },
				{ value: "KY", text: "Kentucky" },
				{ value: "LA", text: "Louisiana" },
				{ value: "ME", text: "Maine" },
				{ value: "MD", text: "Maryland" },
				{ value: "MA", text: "Massachusetts" },
				{ value: "MI", text: "Michigan" },
				{ value: "MN", text: "Minnesota" },
				{ value: "MS", text: "Mississippi" },
				{ value: "MO", text: "Missouri" },
				{ value: "MT", text: "Montana" },
				{ value: "NE", text: "Nebraska" },
				{ value: "NV", text: "Nevada" },
				{ value: "NH", text: "New Hampshire" },
				{ value: "NJ", text: "New Jersey" },
				{ value: "NM", text: "New Mexico" },
				{ value: "NY", text: "New York" },
				{ value: "NC", text: "North Carolina" },
				{ value: "ND", text: "North Dakota" },
				{ value: "OH", text: "Ohio" },
				{ value: "OK", text: "Oklahoma" },
				{ value: "OR", text: "Oregon" },
				{ value: "PA", text: "Pennsylvania" },
				{ value: "RI", text: "Rhode Island" },
				{ value: "SC", text: "South Carolina" },
				{ value: "SD", text: "South Dakota" },
				{ value: "TN", text: "Tennessee" },
				{ value: "TX", text: "Texas" },
				{ value: "UT", text: "Utah" },
				{ value: "VT", text: "Vermont" },
				{ value: "VA", text: "Virginia" },
				{ value: "WA", text: "Washington" },
				{ value: "WV", text: "West Virginia" },
				{ value: "WI", text: "Wisconsin" },
				{ value: "WY", text: "Wyoming" },
			],
			noContacts: false,
			noInitContacts: false,
			showDeleteCustomerDialog: false,
			deletingCustomer: false,
			savingCustomer: false,
			showDeleteRecordDialog: false,
			pendingDeleteData: null,
			pendingDeleteType: "",
			deleteError: false,
			deleteErrorMessage: "",
		};
	},
	apollo: {
		salesReps: {
			query() {
				return gql`
					query {
						salesReps(
							skip: 0
							take: 50
							order: { name: ASC }
							where: { inactiveDate: { eq: null } }
						) {
							items {
								salesRepId
								salesRepNumber
								name
								inactiveDate
								sequence
							}
							pageInfo {
								hasNextPage
								hasPreviousPage
							}
						}
					}
				`;
			},
			update: (data) => data.salesReps.items,
		},
		categories: {
			query() {
				return gql`
					query {
						customerCategories(skip: 0, take: 50) {
							items {
								customerCategoryId
								category
							}
						}
					}
				`;
			},
			update: (data) => data.customerCategories.items,
		},
		paymentTerms: {
			query() {
				return gql`
					query {
						paymentTerms(skip: 0, take: 50) {
							items {
								paymentTermsId
								paymentTermsName
							}
						}
					}
				`;
			},
			update: (data) => data.paymentTerms.items,
		},
		paymentMethods: {
			query() {
				return gql`
					query {
						paymentMethods(skip: 0, take: 50) {
							items {
								paymentMethodId
								paymentMethodName
							}
						}
					}
				`;
			},
			update: (data) => data.paymentMethods.items,
		},
		shippingMethods: {
			query() {
				return gql`
					query {
						shippingMethods(skip: 0, take: 50) {
							items {
								shippingMethodId
								shippingMethodName
							}
						}
					}
				`;
			},
			update: (data) => data.shippingMethods.items,
		},
	},
	methods: {
		renderAddress(addressTypeId) {
			return this.addressTypes.find((a) => a.addressTypeId == addressTypeId).text ?? "";
		},
		getContactName(contactId) {
			if (!contactId) return '';
			if (typeof contactId === 'object') {
				return `${contactId.firstName} ${contactId.lastName}`;
			}
			const contact = this.contactData.find(c => c.contactId === contactId);
			return contact ? `${contact.firstName} ${contact.lastName}` : '';
		},

		addContact() {
			this.noContacts = false;
			const newContact = {
				firstName: "",
				lastName: "",
				workPhoneNumber: "",
				cellPhoneNumber: "",
				email: "",
				primaryContact: false,
				isActive: true,
			};

			this.contactData.push(newContact);

			//set the new contact as the active edit
			this.activeEdit = newContact;
		},

		removeContact(data) {
			if (data.contactId) {
				this.pendingDeleteData = data;
				this.pendingDeleteType = "Contact";
				this.showDeleteRecordDialog = true;
			} else {
				this.contactData = this.contactData.filter((obj) => obj !== data);
			}
		},

		addAddress() {
			this.addressTab = false;
			const newAddress = {
				isPrimaryAddress: false,
				label: "",
				addressTypeId: "",
				contactId: null,
				organization: this.customerData.organization || "",
				department: "",
				addressLine1: "",
				addressLine2: "",
				city: "",
				state: "",
				postalCode: "",
				isActive: true,
			};

			this.addressData.push(newAddress);

			//set the new contact as the active edit
			this.activeEdit = newAddress;
		},

		removeAddress(data) {
			if (data.addressId) {
				this.pendingDeleteData = data;
				this.pendingDeleteType = "Address";
				this.showDeleteRecordDialog = true;
			} else {
				this.addressData = this.addressData.filter((obj) => obj !== data);
			}
		},

		addNote() {
			const newNote = {
				customerId: this.$store.getters.userInfo.name, //the id of the person who added the note to the customer
				note: "",
				date: new Date(),
			};

			this.noteData.push(newNote);

			//set the new contact as the active edit
			this.activeEdit = newNote;
		},

		removeNote(data) {
			if (data.customerNoteId) {
				this.pendingDeleteData = data;
				this.pendingDeleteType = "Note";
				this.showDeleteRecordDialog = true;
			} else {
				this.noteData = this.noteData.filter((obj) => obj !== data);
			}
		},

		async deleteCustomer() {
			this.deletingCustomer = true;
			try {
				await this.$http.delete(`/api/customers/${this.customerData.customerId}`);
				this.showDeleteCustomerDialog = false;
				this.$router.push("/relationships/customer");
			} catch (error) {
				this.showDeleteCustomerDialog = false;
				this.deleteErrorMessage =
					error.response?.data?.error || "Failed to delete customer.";
				this.deleteError = true;
			} finally {
				this.deletingCustomer = false;
			}
		},

		cancelDeleteRecord() {
			this.showDeleteRecordDialog = false;
			this.pendingDeleteData = null;
			this.pendingDeleteType = "";
		},

		async confirmDeleteRecord() {
			const data = this.pendingDeleteData;
			const type = this.pendingDeleteType;
			this.showDeleteRecordDialog = false;

			try {
				if (type === "Contact") {
					await this.$http.delete(`/api/customers/contacts/${data.contactId}`);
					this.contactData = this.contactData.filter((obj) => obj !== data);
				} else if (type === "Address") {
					await this.$http.delete(`/api/customers/addresses/${data.addressId}`);
					this.addressData = this.addressData.filter((obj) => obj !== data);
				} else if (type === "Note") {
					await this.$http.delete(`/api/customers/notes/${data.customerNoteId}`);
					this.noteData = this.noteData.filter((obj) => obj !== data);
				}
			} catch (error) {
				this.deleteErrorMessage = `Failed to delete ${type.toLowerCase()}.`;
				this.deleteError = true;
			}

			this.pendingDeleteData = null;
			this.pendingDeleteType = "";
		},

		async onFileUpload(event) {
			// Open input file window, set file data and display crop modal
			this.uploadedFile =
				event.type == "change" ? event.target.files[0] : event.dataTransfer.files[0];

			// Create a new instance of the FileReader object
			let reader = new FileReader();

			// Define an event handler for the "load" event of the FileReader object
			reader.onload = async (e) => {
				this.uploadedRender = e.target.result;

				// Create a FormData object and append the CustomerId and the file
				const formData = new FormData();
				formData.append("request.CustomerId", this.customerData.customerId);
				formData.append("request.TaxForm", this.uploadedFile);

				// Call the API to store the file in the back-end
				try {
					const response = await this.$http.post(
						getApiUrl("/api/customers/upload"),
						formData,
						{
							headers: {
								"Content-Type": "multipart/form-data",
							},
						}
					);

					if (response.status !== 200) {
						throw new Error("Network response was not ok");
					}

					// Wait 2 seconds for file to upload
					await new Promise((resolve) => setTimeout(resolve, 2000));

					// Read file from Google for newly generated Id
					await this.getUploadedFile();
				} catch (error) {
					console.error("Error uploading file:", error);
				}
			};

			// Instruct the FileReader object to read the uploaded file as a data URL
			reader.readAsDataURL(this.uploadedFile);
		},

		validate() {
			this.noContacts = this.contactData.length === 0;
			const orgValidation = this.$refs.organizationRef && this.$refs.organizationRef.validate();
			const contactValidation =
				this.activeTab !== 1 || (this.$refs.contactRef && this.$refs.contactRef.validate());
			const salesRepValidation = this.$refs.salesRepRef && this.$refs.salesRepRef.validate();
			return orgValidation && contactValidation && salesRepValidation && !this.noContacts;
		},

		async saveCustomer() {
			let isValid = this.validate();

			if (isValid) {
				try {
					let res = this.customerData.customerId
						? await this.$http.put(
								getApiUrl("/api/customers"),
								this.customerData
						  )
						: await this.$http.post(
								getApiUrl("/api/customers"),
								this.customerData
						  );

					//add each customer related fields (contacts, addresses, notes) to the customer
					let customerId = res.data.customerId || this.customerData.customerId;
					// Update local state so subsequent saves use PUT instead of POST
					this.customerData.customerId = customerId;
					if (res.data.customerNumber) {
						this.customerData.customerNumber = res.data.customerNumber;
					}
					if (customerId) {
						for (const contact of this.contactData) {
							contact.customerId = customerId;
							contact.contactTypeId = 1;
							await this.saveContact(contact);
						}
						for (const address of this.addressData) {
							address.customerId = customerId;
							await this.saveAddress(address);
						}
						for (const note of this.noteData) {
							note.customerId = customerId;
							await this.saveNote(note);
						}
					}

					return customerId;
				} catch (error) {
					console.log(error);
					this.$toast.error("Failed to save customer");
				}
			}
			return isValid;
		},

		async saveContact(contact) {
			try {
				//check if contact has an id and either create or update the contact
				if (contact.contactId) {
					await this.$http.put(getApiUrl("/api/customers/contacts"), contact);
				} else {
					const res = await this.$http.post(getApiUrl("/api/customers/contacts"), contact);
					contact.contactId = res.data.contactId;
				}
			} catch (error) {
				console.log(error);
			}
		},

		async saveAddress(address) {
			// Normalize contactId — when a newly-created contact is selected before
			// being saved, Vuetify stores the entire contact object instead of the id
			if (address.contactId && typeof address.contactId === 'object') {
				address.contactId = address.contactId.contactId || null;
			}
			try {
				//check if address has an id and either create or update the address
				if (address.addressId) {
					await this.$http.put(getApiUrl("/api/customers/addresses"), address);
				} else {
					const res = await this.$http.post(getApiUrl("/api/customers/addresses"), address);
					address.addressId = res.data.addressId;
				}
			} catch (error) {
				console.log(error);
			}
		},

		async saveNote(note) {
			try {
				//check if note has a customerNoteId and either create or update the note
				if (note.customerNoteId) {
					await this.$http.put(getApiUrl("/api/customers/notes"), note);
				} else {
					const res = await this.$http.post(getApiUrl("/api/customers/notes"), note);
					note.customerNoteId = res.data.noteId;
				}
			} catch (error) {
				console.log(error);
			}
		},

		async getUploadedFile() {
			// TODO: Get from API
			try {
				if (this.customerId) {
					let response = await this.$http.get(
						getApiUrl(`/api/customers/taxform/${this.customerId}`)
					);
					let file = response.data;
					if (file && Object.keys(file).length > 0) {
						this.uploadedFile = { name: file.fileName };
						this.uploadedFileLink = { name: file.fileName, id: file.fileId };
					}
				}
			} catch (error) {
				console.log(error);
			}
		},
		async handleSaveCustomer() {
			this.savingCustomer = true;
			try {
				const isNew = !this.customerData.customerId;
				let customerId = await this.saveCustomer();
				if (customerId) {
					this.$toast.success("Customer saved successfully");
					if (isNew) {
						// Navigate to the edit route so subsequent saves do PUT, not POST
						this.$router.replace(`/relationships/customer/${customerId}`);
					}
				}
			} catch (error) {
				this.$toast.error("Failed to save customer");
			} finally {
				this.savingCustomer = false;
			}
		},
		async handleSaveCustomerWithReturnToQuote() {
			this.savingCustomer = true;
			try {
				let customerId = await this.saveCustomer();
				if (customerId) {
					this.$toast.success("Customer saved successfully");
					if (this.orderId) {
						this.$router.replace({
							path: `/sales-orders/orders/${this.orderId}`,
						});
					} else if (this.quoteId) {
						// Existing quote — just navigate back; quote reload will pick up updated customer data
						this.$router.replace({
							path: `/sales-orders/quotes/${this.quoteId}`,
						});
					} else {
						// New quote — use initWithCustomer to pre-fill customer
						this.$router.replace({
							path: `/sales-orders/quotes/new?initWithCustomer=${customerId}`,
						});
					}
				}
			} catch (error) {
				this.$toast.error("Failed to save customer");
			} finally {
				this.savingCustomer = false;
			}
		},

		async getCustomer() {
			try {
				const res = await this.$apollo.query({
					query: gql`query {
                        customerById(id: ${this.customerId}) {
                            customerId
                            organization
                            salesTaxId
                            customerNumber
                            shippingMethodId
                            customerCategoryId
                            paymentTermsId
                            salesRepId
                            paymentMethodId
                            contacts {
                                contactId
                                primaryContact
                                firstName
                                lastName
                                workPhoneNumber
                                cellPhoneNumber
                                email
                                isActive
                            }
                            addresses {
                                addressId
                                label
                                addressLine1
                                addressLine2
                                addressTypeId
                                city
                                state
                                postalCode
                                isPrimaryAddress
								isActive
								contactId
								organization
								department
                            }
                            notes {
                                customerId
                                customerNoteId
                                note
                            }
                        }
                    }`,
					fetchPolicy: "no-cache",
				});

				this.customerData = res.data.customerById;
				this.noInitContacts =
					!this.customerData.contacts || this.customerData.contacts.length === 0;

				// salesTaxId: 0 => Exempt, [1, null] => 'Taxed'
				this.customerData = { ...this.customerData, salesTaxId: this.customerData.salesTaxId ?? 0 };
				this.contactData = this.customerData.contacts ?? [];
				this.addressData = this.customerData.addresses ?? [];
				this.noteData = this.customerData.notes ?? [];
			} catch (error) {
				console.log(error);
			}
		},
	},
	async created() {
		await this.getUploadedFile();
	},
	async mounted() {
		if (this.addressTab) {
			this.activeTab = 2;
		}

		if (this.customerId) {
			await this.getCustomer();
		}
	},
};
</script>

<style lang="scss" scoped>
.customer {
	&-actions {
		display: flex;
		width: 100%;
		justify-content: flex-end;
		margin-bottom: 1rem;

		.v-btn {
			background-color: $color-white;
			border-color: $color-gray;
			text-transform: initial;
		}
	}

	&-form {
		position: relative;
		display: block;
		background-color: $color-white;
		border: 1px solid $color-gray;
		border-radius: 5px;
		padding: 0.5rem 1.5rem;

		&-upload {
			display: flex;
			align-items: center;
			padding: 0.5rem 0;
		}
	}

	&-tabs {
		list-style: none;
		display: flex;
		flex-flow: row nowrap;
		padding-left: 0;

		&-tab {
			padding: 0.5rem 1rem;
			color: $color-gray;
			border-bottom: 2px solid transparent;
			cursor: pointer;
			transition: all 0.3s;

			&:hover {
				border-bottom: 2px solid $color-gray;
			}

			&.active {
				color: $color-black;
				border-bottom: 2px solid $color-tertiary;
			}
		}
	}
}
</style>

<style lang="scss">
@keyframes pulse {
	0% {
		transform: scale(1);
		opacity: 1;
	}

	50% {
		transform: scale(1.1);
		opacity: 0.7;
	}

	100% {
		transform: scale(1);
		opacity: 1;
	}
}

.pulse-effect {
	animation: pulse 2s infinite;
}

.table-container .v-select .v-select__selections {
	overflow: hidden;
	flex-wrap: nowrap;
}
</style>
